import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";

/**
 * Reusable HARD-DELETE confirmation dialog.
 *
 * Props:
 *   resourceType   e.g. "account" | "project" | "campaign" (used only in copy)
 *   resourceName   the exact name; user must type it to unlock the button
 *   resourceId     used to build the DELETE URL
 *   endpoint       "/accounts" | "/projects" | "/campaigns"
 *   onDeleted      callback after 200 OK
 *   trigger        optional custom trigger; falls back to a destructive button
 *   testId         base data-testid; buttons get "-btn" and "-confirm" suffixes
 */
export default function HardDeleteDialog({
  resourceType, resourceName, resourceId, endpoint, onDeleted,
  trigger, testId = "hard-delete",
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = typed.trim() === (resourceName || "").trim() && !busy;

  const submit = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const url = `${endpoint}/${resourceId}?hard=true&confirm_name=${encodeURIComponent(resourceName)}`;
      const { data } = await api.delete(url);
      toast.success(`Permanently deleted ${resourceType}`);
      setOpen(false);
      setTyped("");
      onDeleted && onDeleted(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTyped(""); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="destructive" size="sm" data-testid={`${testId}-btn`}>
            <Trash2 className="mr-2 h-3 w-3" />
            Delete permanently
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            PERMANENTLY delete {resourceType}
          </DialogTitle>
          <DialogDescription>
            This will remove <b>{resourceName}</b> and every child resource
            (contacts, campaigns, messages, uploads). It cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <Alert variant="destructive">
          <AlertTitle className="text-sm">Type the name to confirm</AlertTitle>
          <AlertDescription className="text-xs">
            To confirm, type <code className="font-mono px-1 rounded bg-muted">{resourceName}</code> exactly.
          </AlertDescription>
        </Alert>
        <div>
          <Label className="text-xs">Confirmation</Label>
          <Input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={resourceName}
            data-testid={`${testId}-input`}
            className="font-mono"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!canDelete}
            data-testid={`${testId}-confirm`}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
